<script setup lang="ts">
import { Delete, Plus } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, ref } from "vue";
import type { TreeDefinitionDto, TreeLevelDto, TreeNodeDto } from "~/api";
import { apiClient } from "~/utils/apiClient";
import { showError } from "~/utils/handleAPIError";

const props = defineProps<{
  treeDefinition: TreeDefinitionDto;
}>();

const emit = defineEmits<{
  (e: "refresh"): void;
}>();

const isSectorSetup = computed(
  () => props.treeDefinition.treeType === "SectorSetup",
);

// Flatten tree into el-tree format
interface ElTreeNode {
  id: string;
  label: string;
  code?: string | null;
  levelName: string;
  depth: number;
  estimatedUsage: number;
  parentTreeNodeGuid?: string | null;
  finderDisplayName?: string | null;
  finderIsExclusive?: boolean;
  children: ElTreeNode[];
}

const mapToElTreeNodes = (nodes: TreeNodeDto[]): ElTreeNode[] => {
  return nodes.map((node) => ({
    id: node.guid,
    label: node.name,
    code: node.code,
    levelName: node.levelName,
    depth: node.depth,
    estimatedUsage: node.estimatedUsage ?? 0,
    parentTreeNodeGuid: node.parentTreeNodeGuid,
    finderDisplayName: node.finderSettings?.displayName,
    finderIsExclusive: node.finderSettings?.isExclusive,
    children: mapToElTreeNodes(node.children),
  }));
};

const sortNodesAlphabetically = (nodes: ElTreeNode[]): ElTreeNode[] => {
  return [...nodes]
    .sort((a, b) => a.label.localeCompare(b.label))
    .map((node) => ({
      ...node,
      children: sortNodesAlphabetically(node.children),
    }));
};

const treeData = computed(() =>
  sortNodesAlphabetically(mapToElTreeNodes(props.treeDefinition.nodes)),
);

const defaultExpandedKeys = computed(() => {
  // Expand all nodes by default
  const keys: string[] = [];
  const collectKeys = (nodes: TreeNodeDto[]) => {
    for (const node of nodes) {
      keys.push(node.guid);
      if (node.children?.length) {
        collectKeys(node.children);
      }
    }
  };
  collectKeys(props.treeDefinition.nodes);
  return keys;
});

// ---- Add Node Dialog ----
const addNodeDialogVisible = ref(false);
const addNodeParentGuid = ref<string | null>(null);
const addNodeParentName = ref<string>("top level");
const addNodeName = ref("");
const addNodeCode = ref("");
const addNodeLevelGuid = ref("");
const addNodeSaving = ref(false);
const addNodeDisplayName = ref("");
const addNodeIsExclusive = ref(false);

const sortedLevels = computed(() =>
  [...props.treeDefinition.levels].sort((a, b) => a.depth - b.depth),
);

const getNextLevel = (currentDepth: number): TreeLevelDto | undefined => {
  return sortedLevels.value.find((l) => l.depth > currentDepth);
};

const openAddNodeDialog = (parentNode?: ElTreeNode) => {
  addNodeName.value = "";
  addNodeCode.value = "";
  addNodeDisplayName.value = "";
  addNodeIsExclusive.value = false;

  if (parentNode) {
    addNodeParentGuid.value = parentNode.id;
    addNodeParentName.value = parentNode.label;
    const nextLevel = getNextLevel(parentNode.depth);
    if (!nextLevel) {
      ElMessage.warning("No deeper level available. Add a new level first.");
      return;
    }
    addNodeLevelGuid.value = nextLevel.guid;
  } else {
    addNodeParentGuid.value = null;
    addNodeParentName.value = "top level";
    const rootLevel = sortedLevels.value[0];
    if (!rootLevel) {
      ElMessage.warning("No levels defined. Add a level first.");
      return;
    }
    addNodeLevelGuid.value = rootLevel.guid;
  }

  addNodeDialogVisible.value = true;
};

const selectedLevelName = computed(() => {
  const level = props.treeDefinition.levels.find(
    (l) => l.guid === addNodeLevelGuid.value,
  );
  return level?.name ?? "";
});

const addNode = async () => {
  if (!addNodeName.value.trim()) {
    ElMessage.warning("Node name is required");
    return;
  }

  addNodeSaving.value = true;
  try {
    const newNode = await apiClient.tree.createTreeNode(
      props.treeDefinition.guid,
      {
        treeLevelGuid: addNodeLevelGuid.value,
        parentTreeNodeGuid: addNodeParentGuid.value,
        name: addNodeName.value.trim(),
        code: addNodeCode.value.trim() || null,
        sortOrder: 0,
      },
    );

    if (isSectorSetup.value && addNodeDisplayName.value.trim()) {
      try {
        await apiClient.tree.upsertFinderSettingsForNode(newNode.guid, {
          displayName: addNodeDisplayName.value.trim(),
          isExclusive: addNodeIsExclusive.value,
        });
      } catch (fsError) {
        showError(fsError);
        ElMessage.warning(
          "Node was created but Finder Settings failed to save.",
        );
        addNodeDialogVisible.value = false;
        emit("refresh");
        return;
      }
    }

    addNodeDialogVisible.value = false;
    ElMessage.success("Node added");
    emit("refresh");
  } catch (error) {
    showError(error);
  } finally {
    addNodeSaving.value = false;
  }
};

// ---- Edit Node ----
const editNodeDialogVisible = ref(false);
const editNodeGuid = ref("");
const editNodeName = ref("");
const editNodeCode = ref("");
const editNodeSaving = ref(false);
const editNodeDisplayName = ref("");
const editNodeIsExclusive = ref(false);
const editNodeLoadingFs = ref(false);
const editNodeHadFs = ref(false);
const editNodeRemovingFs = ref(false);

const openEditNodeDialog = async (node: ElTreeNode) => {
  editNodeGuid.value = node.id;
  editNodeName.value = node.label;
  editNodeCode.value = node.code ?? "";
  editNodeDisplayName.value = node.finderDisplayName ?? "";
  editNodeIsExclusive.value = node.finderIsExclusive ?? false;
  editNodeHadFs.value = !!node.finderDisplayName;
  editNodeDialogVisible.value = true;

  if (isSectorSetup.value) {
    editNodeLoadingFs.value = true;
    try {
      const fs = await apiClient.tree.getFinderSettingsForNode(node.id);
      if (fs) {
        editNodeDisplayName.value = fs.displayName;
        editNodeIsExclusive.value = fs.isExclusive;
        editNodeHadFs.value = true;
      } else {
        editNodeHadFs.value = false;
      }
    } catch (error) {
      showError(error);
    } finally {
      editNodeLoadingFs.value = false;
    }
  }
};

const removeFinderSettings = async () => {
  try {
    await ElMessageBox.confirm(
      `Remove Finder Setting from "${editNodeName.value}"? This node will no longer appear in the finder.`,
      "Remove Finder Setting",
      {
        confirmButtonText: "Remove",
        cancelButtonText: "Cancel",
        type: "warning",
      },
    );
    editNodeRemovingFs.value = true;
    await apiClient.tree.deleteFinderSettingsForNode(editNodeGuid.value);
    editNodeDisplayName.value = "";
    editNodeIsExclusive.value = false;
    editNodeHadFs.value = false;
    editNodeDialogVisible.value = false;
    ElMessage.success("Finder Setting removed");
    emit("refresh");
  } catch (error) {
    if (error !== "cancel") showError(error);
  } finally {
    editNodeRemovingFs.value = false;
  }
};

const updateNode = async () => {
  if (!editNodeName.value.trim()) {
    ElMessage.warning("Node name is required");
    return;
  }

  editNodeSaving.value = true;
  try {
    await apiClient.tree.updateTreeNode(editNodeGuid.value, {
      name: editNodeName.value.trim(),
      code: editNodeCode.value.trim() || null,
    });

    if (isSectorSetup.value) {
      const displayName = editNodeDisplayName.value.trim();
      try {
        if (displayName) {
          await apiClient.tree.upsertFinderSettingsForNode(editNodeGuid.value, {
            displayName,
            isExclusive: editNodeIsExclusive.value,
          });
        } else if (editNodeHadFs.value) {
          await apiClient.tree.deleteFinderSettingsForNode(editNodeGuid.value);
        }
      } catch (fsError) {
        showError(fsError);
        ElMessage.warning(
          "Node was updated but Finder Settings change failed to save.",
        );
        editNodeDialogVisible.value = false;
        emit("refresh");
        return;
      }
    }

    editNodeDialogVisible.value = false;
    ElMessage.success("Node updated");
    emit("refresh");
  } catch (error) {
    showError(error);
  } finally {
    editNodeSaving.value = false;
  }
};

// ---- Delete Node ----
const deleteNode = async (node: ElTreeNode) => {
  const hasChildren = node.children.length > 0;
  const message = hasChildren
    ? `This will delete "${node.label}" and all its child nodes. Continue?`
    : `Delete "${node.label}"?`;

  try {
    await ElMessageBox.confirm(message, "Confirm Delete", {
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      type: "warning",
    });
    await apiClient.tree.deleteTreeNode(node.id);
    ElMessage.success("Node deleted");
    emit("refresh");
  } catch (error) {
    if (error !== "cancel") {
      showError(error);
    }
  }
};
</script>

<template>
  <div class="TreeNodeManager">
    <!-- Add Node Dialog -->
    <el-dialog v-model="addNodeDialogVisible" title="Add Node" width="450">
      <el-form label-position="top">
        <el-form-item label="Parent">
          <el-input :model-value="addNodeParentName" disabled />
        </el-form-item>
        <el-form-item label="Level">
          <el-input :model-value="selectedLevelName" disabled />
        </el-form-item>
        <el-form-item label="Name">
          <el-input
            v-model="addNodeName"
            placeholder="Node name"
            @keyup.enter="addNode"
          />
        </el-form-item>
        <el-form-item label="Code (optional)">
          <el-input v-model="addNodeCode" placeholder="Short code" />
        </el-form-item>
      </el-form>
      <template v-if="isSectorSetup">
        <el-divider content-position="left">Finder Settings</el-divider>
        <el-form label-position="top">
          <el-form-item label="Display Name">
            <el-input
              v-model="addNodeDisplayName"
              placeholder="e.g., Age 65+"
            />
          </el-form-item>
          <el-form-item label="Exclusive">
            <el-switch v-model="addNodeIsExclusive" />
          </el-form-item>
        </el-form>
      </template>
      <template #footer>
        <el-button @click="addNodeDialogVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="addNodeSaving" @click="addNode">
          Add
        </el-button>
      </template>
    </el-dialog>

    <!-- Edit Node Dialog -->
    <el-dialog v-model="editNodeDialogVisible" title="Edit Node" width="450">
      <el-form label-position="top">
        <el-form-item label="Name">
          <el-input
            v-model="editNodeName"
            placeholder="Node name"
            @keyup.enter="updateNode"
          />
        </el-form-item>
        <el-form-item label="Code (optional)">
          <el-input v-model="editNodeCode" placeholder="Short code" />
        </el-form-item>
      </el-form>
      <template v-if="isSectorSetup">
        <el-divider content-position="left">Finder Settings</el-divider>
        <el-form v-loading="editNodeLoadingFs" label-position="top">
          <el-form-item label="Display Name">
            <el-input
              v-model="editNodeDisplayName"
              placeholder="e.g., Age 65+"
            />
          </el-form-item>
          <el-form-item label="Exclusive (radio) selection">
            <el-switch v-model="editNodeIsExclusive" />
          </el-form-item>
          <el-form-item v-if="editNodeHadFs">
            <el-button
              type="danger"
              plain
              :icon="Delete"
              :loading="editNodeRemovingFs"
              @click="removeFinderSettings"
            >
              Remove Finder Setting
            </el-button>
          </el-form-item>
        </el-form>
      </template>
      <template #footer>
        <el-button @click="editNodeDialogVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="editNodeSaving" @click="updateNode">
          Save
        </el-button>
      </template>
    </el-dialog>

    <div style="margin-bottom: 12px">
      <el-button type="primary" :icon="Plus" @click="openAddNodeDialog()">
        Add Tree
      </el-button>
    </div>

    <el-tree
      :data="treeData"
      node-key="id"
      :default-expanded-keys="defaultExpandedKeys"
      :expand-on-click-node="false"
      :indent="0"
    >
      <template #default="{ data }">
        <div class="tree-node-row">
          <span class="tree-node-label">
            {{ data.label }}
            <span v-if="data.code" class="tree-node-code"
              >({{ data.code }})</span
            >
          </span>
          <div class="tree-node-level">
            <el-tag size="small" type="info">
              {{ data.levelName }}
            </el-tag>
          </div>
          <div
            class="tree-node-actions"
            :style="{ marginLeft: `-${data.depth * 20}px` }"
          >
            <el-button
              text
              type="primary"
              size="small"
              :icon="Plus"
              title="Add child node"
              @click.stop="openAddNodeDialog(data)"
            />
            <el-button
              text
              type="primary"
              size="small"
              title="Edit node"
              @click.stop="openEditNodeDialog(data)"
            >
              Edit
            </el-button>
            <el-button
              text
              type="danger"
              size="small"
              :icon="Delete"
              title="Delete node"
              @click.stop="deleteNode(data)"
            />
          </div>
          <div class="rowGuid" :title="data.id">
            {{ data.id.slice(0, 7) }}
            <!-- show first 7 chars of ID for debugging -->
            <!-- for debugging -->
          </div>
          <el-tag v-if="data.estimatedUsage > 0" size="small">
            {{ data.estimatedUsage }} usage{{
              data.estimatedUsage !== 1 ? "s" : ""
            }}
          </el-tag>
          <el-tag v-if="data.finderDisplayName" type="warning" size="small">
            Finder Setting: {{ data.finderDisplayName
            }}{{ data.finderIsExclusive ? " (exclusive)" : "" }}
          </el-tag>
        </div>
      </template>
    </el-tree>

    <div
      v-if="treeData.length === 0"
      style="
        color: var(--el-text-color-secondary);
        padding: 20px;
        text-align: center;
      "
    >
      No nodes yet. Click "Add Tree" to start building the tree.
    </div>
  </div>
</template>

<style lang="less">
.TreeNodeManager {
  .tree-node-row {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 1em;
  }

  .tree-node-label {
    display: flex;
    align-items: center;
    gap: 4px;
    width: 400px;
    flex-shrink: 0;
    box-sizing: border-box;
    white-space: normal;
    margin-left: 20px;
    text-indent: -20px;
  }
  .tree-node-level {
    width: 150px;
    flex-shrink: 0;
  }
  .el-tree-node__content {
    height: auto;
  }
  .el-tree-node__children {
    margin-left: 20px;
  }

  .tree-node-code {
    color: var(--el-text-color-secondary);
    font-size: 0.85em;
    margin-left: 4px;
    text-indent: 0;
  }

  .tree-node-actions {
    display: flex;
    gap: 2px;
    flex-shrink: 0;
  }

  .rowGuid {
    color: var(--el-text-color-secondary);
    font-size: 0.75em;
    width: 60px;
  }
}
</style>
